import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Chip,
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  DialogContent,
  Stack,
  DialogActions,
  Divider,
  TextField,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  Download,
  Article,
  CheckCircleOutline,
  HourglassEmpty,
} from "@mui/icons-material";
import { useState, useMemo } from "react";
import moment from "moment";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import CommonDialog from "app/components/common/Dialogs/CommonDialog";
import {
  createDueDateThread,
  getDueDateThread,
} from "app/services/orgService/school.service";
import { LoadingButton } from "@mui/lab";

const PLAN_FEATURES = {
  included: [
    "Unlimited Students",
    "Staff Management",
    "Attendance Tracking",
    "Grade Management",
    "Parent Portal Access",
    "Mobile App Access",
    "Email Support",
    "Data Backup",
  ],
  notIncluded: [
    "SMS Integration",
    "WhatsApp Integration",
    "Advanced Analytics",
    "Custom Branding",
  ],
};

const getRequestStatusSx = (status) => {
  if (status === "Approved")
    return { backgroundColor: "#e8f5e9", color: "#2e7d32" };
  if (status === "Rejected")
    return { backgroundColor: "#ffebee", color: "#c62828" };
  return { backgroundColor: "#fff8e1", color: "#f57f17" };
};

const ActiveChip = () => (
  <Chip label="Active" size="small" variant="outlined" color="success" />
);
const VerifiedChip = ({ verification }) => (
  <Chip
    label={verification ? "Verified" : "Not Verified"}
    size="small"
    variant="outlined"
    color={verification ? "success" : "error"}
    // sx={{ backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 500 }}
  />
);

const CurrentSubscriptionDetailsCard = ({
  schoolInfo,
  sessionInfo,
  session,
}) => {
  const [showFeatures, setShowFeatures] = useState(false);
  const [showVolumeDialog, setShowVolumeDialog] = useState(false);
  const [newVolume, setNewVolume] = useState("");
  const [reason, setReason] = useState("");

  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [newDueDate, setNewDueDate] = useState("");
  const [remarks, setRemarks] = useState("");
  const [volumeHistory] = useState([]);

  const maxExtendDate = useMemo(
    () =>
      schoolInfo?.upcomingDueDate
        ? moment(schoolInfo.upcomingDueDate).add(6, "days").format("YYYY-MM-DD")
        : "",
    [schoolInfo?.upcomingDueDate],
  );
  const minExtendDate = useMemo(
    () => moment().add(1, "days").format("YYYY-MM-DD"),
    [],
  );

  const {
    data: dueDateThreadResponse,
    isFetching: dueDateThreadFetching,
    refetch: refetchDueDateThread,
  } = useQuery({
    queryKey: ["getDueDateThread", showExtendDialog],
    queryFn: () => getDueDateThread(),
    enabled: showExtendDialog,
    retry: false,
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || "Failed to fetch extension history",
      );
    },
  });

  const threadHistory = dueDateThreadResponse?.data?.list || [];

  const { mutate: submitExtend, isLoading: submittingExtend } = useMutation(
    createDueDateThread,
    {
      onSuccess: () => {
        toast.success("Due date extension request submitted successfully!");
        setNewDueDate("");
        setRemarks("");
        refetchDueDateThread();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Failed to submit request");
      },
    },
  );

  const handleSchoolAgreementDownload = () => {
    const fileUrl = schoolInfo?.licence_url;

    if (!fileUrl) {
      toast.error("School agreement is not available right now");
      return;
    }

    try {
      const link = document.createElement("a");
      link.href = fileUrl;

      const urlWithoutQuery = fileUrl.split("?")[0] || "";
      const fallbackName = "school-agreement";
      const derivedName =
        decodeURIComponent(urlWithoutQuery.split("/").pop() || "") ||
        fallbackName;

      link.setAttribute("download", derivedName);
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");

      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to download school agreement");
    }
  };

  const handleExtendSubmit = () => {
    submitExtend({ session, newDueDate, remarks });
  };

  return (
    <>
      <Card sx={{ borderRadius: 3, mb: 2 }}>
        <CardHeader
          title={
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Current Subscription Plan
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  Manage your active subscription
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <ActiveChip />
                <VerifiedChip verification={schoolInfo?.verified} />
              </Box>
            </Box>
          }
        />
        <CardContent>
          <Grid item xs={12}>
            <Grid container spacing={1} mb={2}>
              <Grid item xs={6} md={2}>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontWeight={500}
                  gutterBottom
                >
                  Plan Name
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {schoolInfo?.planName}
                  {/* <Typography
                    component="span"
                    fontWeight={700}
                    color="primary"
                    sx={{ cursor: "pointer" }}
                    onClick={() => setShowFeatures(true)}
                  >
                    Enterprise Plus
                  </Typography> */}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontWeight={500}
                  gutterBottom
                >
                  Plan Type
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {schoolInfo?.planType}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontWeight={500}
                  gutterBottom
                >
                  Active/Actual Student
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {sessionInfo?.countActiveStudent}/
                  {sessionInfo?.countActualStudent}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontWeight={500}
                  gutterBottom
                >
                  Student Limit
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body1" fontWeight={600}>
                    {sessionInfo?.studentLimitCount}
                  </Typography>
                  {/* <IconButton
                    size="small"
                    color="secondary"
                    onClick={() => setShowVolumeDialog(true)}
                    sx={{ p: 0.2, backgroundColor: "#e3f2fd" }}
                  >
                    <ArrowDropUp fontSize="medium" />
                  </IconButton> */}
                </Stack>
              </Grid>

              <Grid item xs={6} md={2}>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontWeight={500}
                  gutterBottom
                >
                  License Period
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {moment(schoolInfo?.licenceFrom).format("DD MMM YYYY")} to{" "}
                  {moment(schoolInfo?.licenceTo).format("DD MMM YYYY")}
                </Typography>
              </Grid>
              <Grid item xs={6} md={2}>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontWeight={500}
                  gutterBottom
                >
                  Next Billing Date
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body1" fontWeight={600}>
                    {moment(schoolInfo?.upcomingDueDate).format("DD MMM YYYY")}
                  </Typography>
                  <Chip
                    label="Extend"
                    size="small"
                    onClick={() => setShowExtendDialog(true)}
                    sx={{
                      cursor: "pointer",
                      bgcolor: "#fff3e0",
                      color: "#e65100",
                      fontWeight: 500,
                      "&:hover": { bgcolor: "#ffe0b2" },
                    }}
                  />
                </Stack>
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid item xs={6} md={2}>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontWeight={500}
                  gutterBottom
                >
                  School Agreement
                </Typography>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Download />}
                  onClick={handleSchoolAgreementDownload}
                  disabled={!schoolInfo?.licence_url}
                >
                  Download
                </Button>
              </Grid>

              <Grid item xs={12} md={8}>
                <Alert severity="warning" sx={{ borderRadius: 1, mt: 1.5 }}>
                  ERP access will automatically stop if payment is not completed
                  after the due date. All users will be locked out until payment
                  is received.
                </Alert>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Plan Details Dialog */}
      <CommonDialog
        open={showFeatures}
        onClose={() => setShowFeatures(false)}
        title="Enterprise Plus – Plan Details"
        minWidth="600px"
      >
        <DialogContent>
          {/* Feature Comparison */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="body1" fontWeight={600} mb={1}>
                Included Features
              </Typography>
              <List dense disablePadding>
                {PLAN_FEATURES.included.map((f) => (
                  <ListItem key={f} disableGutters sx={{ py: 0.2 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <CheckCircle sx={{ color: "#4caf50", fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={f}
                      primaryTypographyProps={{ variant: "body1" }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body1" fontWeight={600} mb={1}>
                Available as Add-ons
              </Typography>
              <List dense disablePadding>
                {PLAN_FEATURES.notIncluded.map((f) => (
                  <ListItem key={f} disableGutters sx={{ py: 0.2 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <Cancel sx={{ color: "#9e9e9e", fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={f}
                      primaryTypographyProps={{
                        variant: "body1",
                        color: "text.secondary",
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </DialogContent>

        <Divider />

        <DialogActions>
          <Button
            onClick={() => setShowFeatures(false)}
            color="primary"
            variant="outlined"
          >
            Close
          </Button>
          <Button variant="contained" color="secondary">
            Marketplace
          </Button>
        </DialogActions>
      </CommonDialog>

      {/* Volume Change Dialog */}
      <CommonDialog
        open={showVolumeDialog}
        onClose={() => {
          setShowVolumeDialog(false);
          setNewVolume("");
          setReason("");
        }}
        title="Request Volume Change"
        minWidth="620px"
      >
        <DialogContent>
          <Typography variant="body1" color="text.secondary" mb={2}>
            Request changes to your student volume allocation
          </Typography>

          <Grid container spacing={2} mb={3}>
            {/* Left column */}
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                <TextField
                  label="Current Volume (Students)"
                  value={sessionInfo?.studentLimitCount ?? ""}
                  disabled
                  size="small"
                />
                <TextField
                  label="New Requested Volume"
                  value={newVolume}
                  onChange={(e) =>
                    setNewVolume(e.target.value.replace(/\D/g, ""))
                  }
                  inputProps={{ inputMode: "numeric" }}
                  placeholder="Enter new volume"
                  size="small"
                />
              </Stack>
            </Grid>

            {/* Right column */}
            <Grid item xs={12} md={6}>
              <Stack spacing={2} height="100%">
                <TextField
                  label="Reason for Change"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Explain why you need to change the volume..."
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                />
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  disabled={!newVolume || !reason}
                  onClick={() => {}}
                >
                  Submit Request
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {/* Request History */}
          <Divider sx={{ mb: 2 }} />
          <Typography variant="Subtitle1" fontWeight={600} mb={1.5}>
            Request History
          </Typography>

          {volumeHistory.length === 0 ? (
            <Typography
              variant="Subtitle2"
              color="text.secondary"
              sx={{ py: 1 }}
            >
              No volume change requests found.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {volumeHistory.map((req, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "grey.50",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="flex-start"
                    >
                      <Box mt={0.3}>
                        {req.status === "Approved" ? (
                          <CheckCircleOutline
                            sx={{ color: "success.main", fontSize: 20 }}
                          />
                        ) : (
                          <HourglassEmpty
                            sx={{ color: "warning.main", fontSize: 20 }}
                          />
                        )}
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          Volume Change: {req.from} → {req.to}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Requested on{" "}
                          {moment(req.requestedOn).format("MMM DD, YYYY")}
                        </Typography>
                        <Typography
                          variant="Subtitle2"
                          color="text.secondary"
                          mt={0.3}
                        >
                          Reason: {req.reason}
                        </Typography>
                        {req.respondedOn && (
                          <Typography variant="caption" color="text.secondary">
                            Response received on{" "}
                            {moment(req.respondedOn).format("MMM DD, YYYY")}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    <Chip
                      label={req.status}
                      size="small"
                      sx={{
                        ...getRequestStatusSx(req.status),
                        fontWeight: 500,
                        flexShrink: 0,
                      }}
                    />
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
      </CommonDialog>

      {/* Extend Due Date Dialog */}
      <CommonDialog
        open={showExtendDialog}
        onClose={() => {
          setShowExtendDialog(false);
          setNewDueDate("");
          setRemarks("");
        }}
        title="Extend Due Date"
        minWidth="560px"
      >
        <DialogContent>
          <Typography variant="subtitle1" color="text.secondary" mb={2}>
            Request an extension for the upcoming billing due date. Maximum
            extension allowed is <strong>6 days</strong> from the current due
            date.
          </Typography>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Current Due Date"
                value={
                  schoolInfo?.upcomingDueDate
                    ? moment(schoolInfo.upcomingDueDate).format("DD MMM YYYY")
                    : ""
                }
                disabled
                fullWidth
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="New Due Date"
                type="date"
                value={newDueDate}
                onChange={(e) => setNewDueDate(e.target.value)}
                inputProps={{ min: minExtendDate, max: maxExtendDate }}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                helperText={`Select a date up to ${maxExtendDate ? moment(maxExtendDate).format("DD MMM YYYY") : "—"}`}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Reason for requesting extension..."
                multiline
                fullWidth
                rows={3}
                size="small"
              />
            </Grid>

            <Grid item xs={6}>
              <LoadingButton
                loading={submittingExtend}
                variant="contained"
                color="warning"
                size="medium"
                disabled={!newDueDate || !remarks || !session}
                onClick={handleExtendSubmit}
              >
                Submit Extension Request
              </LoadingButton>
            </Grid>
          </Grid>

          {/* Thread History */}
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h6" fontWeight={600} mb={1.5}>
            Request History
          </Typography>

          {dueDateThreadFetching && (
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ py: 1 }}
            >
              Loading request history...
            </Typography>
          )}

          {!dueDateThreadFetching && threadHistory.length === 0 && (
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ py: 1 }}
            >
              No extension requests found.
            </Typography>
          )}

          {!dueDateThreadFetching && threadHistory.length > 0 && (
            <Stack spacing={1.5}>
              {threadHistory.map((thread, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "grey.50",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {moment(thread.currentDate).format("DD MMM YYYY")} →{" "}
                        {moment(thread.newDueDate).format("DD MMM YYYY")}
                      </Typography>
                      <Typography variant="subtitle1" color="text.secondary">
                        Requested on{" "}
                        {moment(thread.createdOn).format("DD MMM YYYY")} by{" "}
                        {thread.createdBy?.name ?? "Admin"}
                      </Typography>
                      {thread.remarks && (
                        <Typography
                          variant="subtitle1"
                          color="text.secondary"
                          mt={0.3}
                        >
                          Remarks: {thread.remarks}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={thread.status ?? "Pending"}
                      size="medium"
                      sx={{
                        ...getRequestStatusSx(thread.status ?? "Pending"),
                        fontWeight: 500,
                        fontSize: "0.9rem",
                        flexShrink: 0,
                      }}
                    />
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
      </CommonDialog>
    </>
  );
};

export default CurrentSubscriptionDetailsCard;
